import React from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from '@apollo/react-hooks'

import { getConfigValue } from '../../../../utils/getConfigValue'
import Input from '../../../../components/form-fields/input'
import Select from '../../../../components/form-fields/select'
import Button from '../../../../components/button'
import FullScreenLoadingComponent from '../../../../components/loading/fullscreen-loader'

import {
  SIGN_UP_SCREENS,
  STATIC_SCREENS,
  FORM_MESSAGES,
  FORM_VALUES
} from '../../../../constants'
import { useConfig } from '../../../../hooks/use-config'
import { handleGATrack } from '../../utils/handle-ga-track'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { segmentTrackSignup } from '../../../../segment/segment-track'
import getGenderOptions from '../../../../utils/getGenderOptions'
import { updateAuthToken } from '../../../../actions'
import ACCOUNT_MUTATION from '../../../../../graphql/mutations/account.gql'
import styles from '../sign-up.css'

/**
 * AccountDetailsForm component for entering a new user's account details
 */
const AccountDetailsForm = ({ voucher }) => {
  const currentScreen = SIGN_UP_SCREENS.ACCOUNT_DETAILS
  const history = useHistory()
  const dispatch = useDispatch()

  const [updateAccount, { loading: isUpdatingAccount }] = useMutation(ACCOUNT_MUTATION)

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitted },
    setError,
    getValues,
    trigger,
    getFieldState
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      birthYear: '',
      gender: ''
    }
  })

  // Birth year validation constants
  const { appConfig } = useConfig()
  const currentYear = new Date().getFullYear()
  const ACCOUNT_MIN_AGE = getConfigValue(appConfig.values, 'ACCOUNT_SIGNUP_MIN_AGE')
  const ACCOUNT_MAX_AGE = getConfigValue(appConfig.values, 'ACCOUNT_SIGNUP_MAX_AGE')
  const minBirthYear = currentYear - ACCOUNT_MAX_AGE
  const maxBirthYear = currentYear - ACCOUNT_MIN_AGE

  // Gender options
  const genderOptions = getGenderOptions()

  // Report validation errors when form submission fails validation
  const reportValidationErrors = (validationErrors) => {
    Object.keys(validationErrors).forEach((fieldName) => {
      const error = validationErrors[fieldName]
      const values = getValues()

      handleGATrack(
        { name: fieldName, value: values[fieldName] },
        { message: error.message },
        currentScreen.datalayerStepSequence,
        currentScreen.datalayerStepLabel,
        true
      )
    })
  }

  const onSubmit = async (formData) => {
    try {
      const { data } = await updateAccount({
        variables: {
          input: {
            name: formData.firstName,
            surname: formData.lastName,
            birthYear: formData.birthYear,
            gender: formData.gender
          }
        }
      })

      // Destructure account from response
      const account = data?.account

      // Handle GraphQL errors
      const firstError = account?.errors?.[0]?.message
      if (firstError) {
        setError('root.formError', { message: firstError })
        return
      }

      // If we have a token, update the auth token in Redux
      // This allows us to send the updated user information (e.g. first name and last name) to the data layer
      const token = account?.session?.token
      if (token) {
        dispatch(updateAuthToken(token, true))
      }

      // Add segment data analytics for sign up - account details
      segmentTrackSignup({
        voucher: voucher ?? null,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        birthYear: formData.birthYear
      })

      // Navigate to payment page on success
      history.push({
        pathname: SIGN_UP_SCREENS.PAYMENT.path,
        search: qs.stringify({
          ...qs.parse(location.search)
        })
      })
    } catch (error) {
      // Handle network or GraphQL errors
      const errorMessage = error.graphQLErrors
        ? getGQLErrorMsg(error)
        : error.message || 'An error occurred. Please try again.'

      setError('root.formError', {
        message: errorMessage
      })
    }
  }

  return (
    <>
      <form
        onSubmit={handleFormSubmit(onSubmit, reportValidationErrors)}
        autoComplete="off"
      >
        <Controller
          name="firstName"
          control={control}
          rules={{
            required: FORM_MESSAGES.required
          }}
          render={({ field }) => (
            <Input
              label="First name"
              {...field}
              error={errors[field.name]?.message}
              onBlur={() => {
                field.onBlur()
                handleGATrack(field, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              disabled={isUpdatingAccount}
              data-e2e="first-name-signup"
              autoComplete="new-off"
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{
            required: FORM_MESSAGES.required
          }}
          render={({ field }) => (
            <Input
              label="Last name"
              {...field}
              error={errors[field.name]?.message}
              onBlur={() => {
                field.onBlur()
                handleGATrack(field, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              disabled={isUpdatingAccount}
              data-e2e="last-name-signup"
              autoComplete="new-off"
            />
          )}
        />

        <Controller
          name="birthYear"
          control={control}
          rules={{
            required: FORM_MESSAGES.required,
            pattern: {
              value: FORM_VALUES.birthYear.pattern,
              message: FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
            },
            minLength: {
              value: FORM_VALUES.birthYear.length,
              message: FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
            },
            maxLength: {
              value: FORM_VALUES.birthYear.length,
              message: FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
            },
            validate: (value) => {
              const year = parseInt(value, 10)
              if (isNaN(year)) return FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
              if (year < minBirthYear || year > maxBirthYear) {
                return FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
              }
              return true
            }
          }}
          render={({ field }) => (
            <Input
              label="Year of birth (YYYY)"
              {...field}
              error={errors[field.name]?.message}
              onBlur={async () => {
                field.onBlur()
                await trigger(field.name)
                // Get the latest validation state for the field immediately after trigger()
                const fieldState = getFieldState(field.name)
                handleGATrack(field, fieldState.error, currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, true)
              }}
              tooltip="We’ll use this information to create a more personalised NEON experience"
              disabled={isUpdatingAccount}
              data-e2e="year-of-birth-signup"
              autoComplete="new-off"
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          rules={{
            required: FORM_MESSAGES.required
          }}
          render={({ field }) => (
            <Select
              label="Gender"
              {...field}
              error={errors[field.name]?.message}
              onChange={(newValue) => {
                field.onChange(newValue)
                handleGATrack({ ...field, value: newValue }, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              options={genderOptions}
              disabled={isUpdatingAccount}
              data-e2e="gender-signup"
            />
          )}
        />

        <div className={styles.greenLink}>
          NEON will use your data to personalise and improve your NEON experience and to send you information about NEON. You can change your communication preferences at any time. We may use your data as described in our&nbsp;
          <a href={STATIC_SCREENS.PRIVACY} target="_blank" rel="noopener noreferrer">
            Privacy Policy.
          </a>
        </div>

        <Button
          className={styles.submit}
          text="Set up payment"
          type="submit"
          disabled={isUpdatingAccount}
          e2eIdentifier="setup-payment-button-signup-details-submit"
        />
        {errors.root?.formError && (
          <p className={styles.errorMessage}>
            {errors.root.formError.message}
          </p>
        )}
      </form>

      <FullScreenLoadingComponent visible={isUpdatingAccount} data-e2e="signup-loader-visible" />
    </>
  )
}

AccountDetailsForm.propTypes = {
  voucher: PropTypes.string
}

export default AccountDetailsForm
